import type { OidcClient } from '@axa-fr/react-oidc';
import { SESSION_EXPIRED_PATH } from '@healthhub/message-channel';
import { clearAllUserSession, MessageChannelBridge } from '@healthhub/react-ui';
import { Modal } from 'antd';

import { showErrorModal } from '@shared/components/Modals/utils';
import { sitecoreHomeBaseUrl } from '@shared/configs/configs/config';
import { persistor } from '@shared/stores';
import { MESSAGE_ACTION } from '@shared/utils/bridge';
import { clearCookies } from '@shared/utils/cookie';

interface IHandleAuthErrorProps {
  isForbidden: boolean;
  isMobileView: boolean;
  isPortalView: boolean;
  isUnauthorised: boolean;
  oidc: OidcClient;
}

// TO REMOVE AFTER NO CHANNEL APP SUPPORT, SHOULD NOT USE THIS ANYMORE
const UNAUTHORISED_PATH = '?action=unauthorised';

const getNavigationPath = ({
  isForbidden,
  isMobileView,
  isPortalView,
  isUnauthorised,
}: Omit<IHandleAuthErrorProps, 'oidc'>): string | undefined => {
  // Unauthorized in portal/mobile -> session expired page
  if (isUnauthorised && (isMobileView || isPortalView)) {
    return SESSION_EXPIRED_PATH;
  }

  // Unauthorized in standard view -> unauthorized
  if ((isUnauthorised || isForbidden) && !isMobileView && !isPortalView) {
    return UNAUTHORISED_PATH;
  }

  return undefined;
};

let isForbiddenModalOpen = false;

// Resolves once the user taps OK. Kept as a promise so the interceptor holds the failed request
// until then — otherwise the caller could react (e.g. its own error modal) and Modal.destroyAll
// would close this modal without ever sending the native "back"
const showForbiddenModal = () =>
  new Promise<void>((resolve) => {
    // Concurrent 403s share the first modal; later calls stay pending so "back" and logout fire once
    if (isForbiddenModalOpen) return;
    isForbiddenModalOpen = true;
    showErrorModal({
      content: 'Singpass login is required for this service',
      onClose: () => {
        Modal.destroyAll();
        isForbiddenModalOpen = false;
        resolve();
      },
    });
  });

export const handleAuthError = async ({
  isForbidden,
  isMobileView,
  isPortalView,
  isUnauthorised,
  oidc,
}: IHandleAuthErrorProps) => {
  clearAllUserSession({ persistor, clearCookies });

  if (isForbidden && isMobileView) {
    await showForbiddenModal();
    MessageChannelBridge.sendMessageToNativeBridge(MESSAGE_ACTION.back());
    return;
  }

  const logoutUrl =
    isForbidden && isPortalView
      ? sitecoreHomeBaseUrl()
      : getNavigationPath({ isForbidden, isMobileView, isPortalView, isUnauthorised });
  await oidc.logoutAsync(logoutUrl);
};
